import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { CircleOff } from 'lucide-react';
import {
  KorzaStudioBackdrop,
  KorzaStudioBadge,
} from '@/pages/onboarding/mockup/korza-studio-backdrop';
import { ZoomedIPhoneMockup } from '@/pages/onboarding/mockup/zoomed-iphone-mockup';
import {
  arePillsClear,
  frameStudioDevice,
  type StudioAnchor,
} from './studio-framing';

// IPhoneMockup's default: the scenes (and the push) are drawn for it, so the
// whole device is scaled instead of being rendered at another `screenWidth`.
const SCREEN_WIDTH = 300;
const BADGE_MIN_STAGE_PX = 640;

/** Measures the stage and the (unscaled) device, and frames the device. */
function useStudioFraming(anchor: StudioAnchor) {
  const stageRef = useRef<HTMLDivElement>(null);
  const deviceRef = useRef<HTMLDivElement>(null);
  const [sizes, setSizes] = useState({
    stageWidth: 0,
    stageHeight: 0,
    deviceWidth: 0,
    deviceHeight: 0,
  });

  useLayoutEffect(() => {
    const stage = stageRef.current;
    const device = deviceRef.current;
    if (!stage || !device) return;
    // offsetWidth/offsetHeight ignore the transform: the device's own size.
    const measure = () =>
      setSizes({
        stageWidth: stage.clientWidth,
        stageHeight: stage.clientHeight,
        deviceWidth: device.offsetWidth,
        deviceHeight: device.offsetHeight,
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  const measured = sizes.deviceWidth > 0 && sizes.stageHeight > 0;
  return {
    stageRef,
    deviceRef,
    measured,
    stageWidth: sizes.stageWidth,
    framing: frameStudioDevice({ ...sizes, anchor }),
  };
}

export interface KorzaStudioStageProps {
  testId: string;
  /** Accessible name of the preview. */
  label: string;
  /** What is being edited, observable as `data-focus`. */
  focus: string;
  /** The end of the device kept in view when the stage zooms in. */
  anchor: StudioAnchor;
  sceneKey: string;
  /** The scene draws its own status bar and home indicator. */
  chromeBaked?: boolean;
  /** "Desligado no app": the feature is off; shown above the other pills. */
  offLabel?: string | null;
  /** More pills above the device; `clear` is false when they would cover the screen. */
  pills?: (state: { clear: boolean }) => ReactNode;
  /** The screen. */
  children: ReactNode;
}

/**
 * The Korza Studio canvas of the visual editors (welcome notification, coupon
 * banner): wavy backdrop, the onboarding's iPhone and the "Studio" badge. The
 * device shows whole on a roomy stage; on a short one (a phone's canvas over
 * the editor sheet) it zooms in on `anchor`, as the onboarding does.
 */
export function KorzaStudioStage({
  testId,
  label,
  focus,
  anchor,
  sceneKey,
  chromeBaked = false,
  offLabel = null,
  pills,
  children,
}: KorzaStudioStageProps) {
  const { stageRef, deviceRef, measured, stageWidth, framing } =
    useStudioFraming(anchor);
  const narrow = stageWidth < BADGE_MIN_STAGE_PX;

  return (
    <section
      data-testid={testId}
      data-zoomed={framing.zoomed || undefined}
      data-focus={focus}
      aria-label={label}
      className="relative h-full min-h-0 overflow-hidden"
    >
      <KorzaStudioBackdrop />
      <div ref={stageRef} className="absolute inset-0 z-10">
        <div
          ref={deviceRef}
          data-platform="ios"
          className={`absolute left-1/2 top-0 -translate-x-1/2 ${measured ? '' : 'invisible'}`}
        >
          <ZoomedIPhoneMockup
            // Mounted again once measured, so it starts framed (not zooming
            // in from the probe's size).
            key={measured ? 'framed' : 'probe'}
            zoomed
            zoomScale={framing.scale}
            zoomOrigin="50% 0%"
            zoomTranslateY={framing.top}
            sceneKey={sceneKey}
            screenWidth={SCREEN_WIDTH}
            chromeBaked={chromeBaked}
          >
            {children}
          </ZoomedIPhoneMockup>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-4 z-20 flex flex-col items-center gap-2 px-4">
        {offLabel && (
          <span className="flex h-[30px] items-center gap-2 rounded-full bg-[#15191E] pl-2.5 pr-3.5 text-[13px] font-semibold text-white">
            <CircleOff aria-hidden strokeWidth={1.5} className="size-4" />
            {offLabel}
          </span>
        )}
        {pills?.({ clear: arePillsClear(framing, anchor) })}
      </div>

      {/* On a phone's canvas it would sit on the device itself. */}
      {measured && !framing.zoomed && !narrow && <KorzaStudioBadge />}
    </section>
  );
}
